/**
 * Telemetría de rendimiento del bucle de captura (diagnóstico del incidente del piloto: un Android
 * lento leía 1–3 frames distintos de la ráfaga de 6 y la marca fallaba con `TOO_FEW_FRAMES`).
 *
 * Mide, por **ventana** (búsqueda del Bootstrap Code; ráfaga del challenge):
 *
 * - cuántas vueltas dio el bucle y con qué cadencia real llegaban los fotogramas de la cámara
 *   (intervalo entre callbacks de `requestVideoFrameCallback`, y `presentedFrames` si existe: si
 *   la cámara presenta 30 fps y el bucle solo ve 10, el hilo principal está saturado);
 * - cuántos fotogramas se mandaron a decodificar, cuántos se descartaron (workers ocupados o
 *   escalonamiento) y cuántos dieron lectura;
 * - mediana y p90 de `decodeMs` (worker), de `drawReducedRoi` (hilo principal) y de la ida y vuelta
 *   completa al worker.
 *
 * **Solo números del equipo**, nunca contenido de los fotogramas ni nada que identifique al
 * trabajador. Memoria acotada: cada serie guarda como mucho `MAX_SAMPLES` muestras (las más
 * recientes), así que una búsqueda larga no crece sin límite.
 *
 * El envío al servidor pasa por `toCaptureTelemetry`, que **recorta a los límites del contrato**:
 * un valor fuera de rango haría fallar la validación de `verify` y con ella la marcación entera, y
 * la telemetría jamás puede costar una marca.
 */
import { CAPTURE_COUNT_MAX, CAPTURE_MS_MAX } from '@fermin/contracts';
import type { CaptureCameraTelemetry, CaptureScanTelemetry } from '@fermin/contracts';

import type { CameraTelemetry } from './camera';

/** Muestras que se conservan por serie (las más recientes). */
export const MAX_SAMPLES = 256;

/** Una serie acotada: anillo de las últimas `MAX_SAMPLES` muestras. */
class SampleRing {
  private readonly values: number[] = [];
  private next = 0;

  push(value: number): void {
    if (!Number.isFinite(value) || value < 0) {
      return;
    }
    if (this.values.length < MAX_SAMPLES) {
      this.values.push(value);
      return;
    }
    this.values[this.next] = value;
    this.next = (this.next + 1) % MAX_SAMPLES;
  }

  clear(): void {
    this.values.length = 0;
    this.next = 0;
  }

  /** Percentil por rango más cercano; `null` sin muestras. */
  percentile(p: number): number | null {
    if (this.values.length === 0) {
      return null;
    }
    const sorted = [...this.values].sort((a, b) => a - b);
    const rank = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
    return sorted[rank] ?? null;
  }
}

/** Resumen de una ventana, en camelCase (interno de Staff). */
export interface ScanStatsSnapshot {
  windowMs: number;
  workers: number;
  ticks: number;
  framesPresented: number | null;
  frameIntervalMsP50: number | null;
  decodeAttempts: number;
  skippedBusy: number;
  skippedStagger: number;
  decodeFailures: number;
  reads: number;
  decodeMsP50: number | null;
  decodeMsP90: number | null;
  drawMsP50: number | null;
  drawMsP90: number | null;
  roundtripMsP50: number | null;
  roundtripMsP90: number | null;
}

/**
 * Lo que el bucle de captura reporta. Todos los métodos son baratos (O(1) salvo `snapshot`) porque
 * se llaman a ritmo de cámara, en el hilo principal.
 */
export interface ScanStats {
  /** Workers del decodificador (lo fija el bucle al arrancar). */
  setWorkers(workers: number): void;
  /** Una vuelta del bucle con fotograma. `presentedFrames` de rVFC si lo hubo. */
  tick(atMs: number, presentedFrames?: number | null): void;
  skippedBusy(): void;
  skippedStagger(): void;
  /** Costo de `drawReducedRoi` y de que el fotograma salga hacia el worker. */
  drawn(drawMs: number): void;
  /** El worker respondió: con lectura (`read`), sin QR, o con fallo (`failed`). */
  decoded(result: { decodeMs: number | null; roundtripMs: number; read: boolean; failed: boolean }): void;
  /** Resumen de la ventana en curso. */
  snapshot(nowMs: number): ScanStatsSnapshot;
  /** Abre una ventana nueva desde `nowMs`. */
  reset(nowMs: number): void;
}

export function createScanStats(startMs: number): ScanStats {
  let windowStartMs = startMs;
  let workers = 1;
  let ticks = 0;
  let lastTickMs: number | null = null;
  let firstPresented: number | null = null;
  let lastPresented: number | null = null;
  let decodeAttempts = 0;
  let skippedBusy = 0;
  let skippedStagger = 0;
  let decodeFailures = 0;
  let reads = 0;
  const intervals = new SampleRing();
  const decodeMs = new SampleRing();
  const drawMs = new SampleRing();
  const roundtripMs = new SampleRing();

  return {
    setWorkers(value) {
      workers = Math.max(1, Math.floor(value));
    },
    tick(atMs, presentedFrames) {
      ticks += 1;
      if (lastTickMs !== null) {
        intervals.push(atMs - lastTickMs);
      }
      lastTickMs = atMs;
      if (typeof presentedFrames === 'number' && Number.isFinite(presentedFrames)) {
        if (firstPresented === null) {
          firstPresented = presentedFrames;
        }
        lastPresented = presentedFrames;
      }
    },
    skippedBusy() {
      skippedBusy += 1;
    },
    skippedStagger() {
      skippedStagger += 1;
    },
    drawn(ms) {
      decodeAttempts += 1;
      drawMs.push(ms);
    },
    decoded(result) {
      roundtripMs.push(result.roundtripMs);
      if (result.failed) {
        decodeFailures += 1;
        return;
      }
      if (result.decodeMs !== null) {
        decodeMs.push(result.decodeMs);
      }
      if (result.read) {
        reads += 1;
      }
    },
    snapshot(nowMs) {
      return {
        windowMs: Math.max(0, nowMs - windowStartMs),
        workers,
        ticks,
        framesPresented:
          firstPresented === null || lastPresented === null
            ? null
            : Math.max(0, lastPresented - firstPresented),
        frameIntervalMsP50: intervals.percentile(0.5),
        decodeAttempts,
        skippedBusy,
        skippedStagger,
        decodeFailures,
        reads,
        decodeMsP50: decodeMs.percentile(0.5),
        decodeMsP90: decodeMs.percentile(0.9),
        drawMsP50: drawMs.percentile(0.5),
        drawMsP90: drawMs.percentile(0.9),
        roundtripMsP50: roundtripMs.percentile(0.5),
        roundtripMsP90: roundtripMs.percentile(0.9),
      };
    },
    reset(nowMs) {
      windowStartMs = nowMs;
      ticks = 0;
      // La cadencia se mide entre vueltas: la primera de la ventana nueva no tiene anterior.
      lastTickMs = null;
      firstPresented = null;
      lastPresented = null;
      decodeAttempts = 0;
      skippedBusy = 0;
      skippedStagger = 0;
      decodeFailures = 0;
      reads = 0;
      intervals.clear();
      decodeMs.clear();
      drawMs.clear();
      roundtripMs.clear();
    },
  };
}

function count(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(CAPTURE_COUNT_MAX, Math.max(0, Math.round(value)));
}

function ms(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  // Una décima de ms basta para diagnosticar y no añade bits inútiles al log.
  return Math.min(CAPTURE_MS_MAX, Math.max(0, Math.round(value * 10) / 10));
}

function msOrNull(value: number | null): number | null {
  return value === null || !Number.isFinite(value) ? null : ms(value);
}

/** Resumen → `CaptureScanTelemetrySchema`, recortado a sus límites (nunca puede invalidar el cuerpo). */
export function toCaptureTelemetry(snapshot: ScanStatsSnapshot): CaptureScanTelemetry {
  return {
    window_ms: ms(snapshot.windowMs),
    workers: Math.min(8, Math.max(1, Math.round(Number.isFinite(snapshot.workers) ? snapshot.workers : 1))),
    ticks: count(snapshot.ticks),
    frames_presented: snapshot.framesPresented === null ? null : count(snapshot.framesPresented),
    frame_interval_ms_p50: msOrNull(snapshot.frameIntervalMsP50),
    decode_attempts: count(snapshot.decodeAttempts),
    skipped_busy: count(snapshot.skippedBusy),
    skipped_stagger: count(snapshot.skippedStagger),
    decode_failures: count(snapshot.decodeFailures),
    reads: count(snapshot.reads),
    decode_ms_p50: msOrNull(snapshot.decodeMsP50),
    decode_ms_p90: msOrNull(snapshot.decodeMsP90),
    draw_ms_p50: msOrNull(snapshot.drawMsP50),
    draw_ms_p90: msOrNull(snapshot.drawMsP90),
    roundtrip_ms_p50: msOrNull(snapshot.roundtripMsP50),
    roundtrip_ms_p90: msOrNull(snapshot.roundtripMsP90),
  };
}

function dimension(value: number | null): number | null {
  if (value === null || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.min(16_384, Math.round(value));
}

/** `CameraTelemetry` → `CaptureCameraTelemetrySchema`, sin `facingMode` y recortado a sus límites. */
export function toCameraTelemetry(telemetry: CameraTelemetry): CaptureCameraTelemetry {
  const frameRate = telemetry.frameRate;
  return {
    width: dimension(telemetry.width),
    height: dimension(telemetry.height),
    frame_rate:
      frameRate === null || !Number.isFinite(frameRate) || frameRate < 0
        ? null
        : Math.min(1_000, Math.round(frameRate * 10) / 10),
    downgraded: telemetry.downgraded,
  };
}
